import React, { useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { toast } from "sonner";
import { MapPin, Phone, Mail, MessageCircle, Send, Loader2, CheckCircle2, Instagram, Facebook } from "lucide-react";
import { PageHero } from "./Universita";
import { UNIVERSITIES, findUniversity, LOGOS } from "../data/universities";
import { submitContact } from "../lib/api";

const TABS = [
  { id: "contact", label: "Richiedi Informazioni" },
  { id: "enrollment", label: "Iscriviti Ora" },
];

const MODALITA = [
  "Standard – Iscrizione diretta",
  "Convenzione Polo Profili (-50%)",
  "Convenzione PA / Forze Armate",
  "Convenzione Sanità / Docenti",
  "Studente Lavoratore – Rate mensili",
];

const empty = (uni = "") => ({
  nome: "",
  cognome: "",
  email: "",
  telefono: "",
  residenza: "",
  universita: uni,
  corso: "",
  modalita_iscrizione: "",
  messaggio: "",
  gdpr_consent: false,
});

export default function Contatti() {
  const location = useLocation();
  const preUni = location.state?.uni || "";
  const preCorso = location.state?.corso || "";
  const [tab, setTab] = useState("contact");
  const [form, setForm] = useState({ ...empty(preUni), corso: preCorso });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const currentUni = findUniversity(form.universita);
  const courseOptions = currentUni
    ? [...currentUni.triennali.map((c) => ({ c, g: "Triennale" })), ...currentUni.magistrali.map((c) => ({ c, g: "Magistrale" })), ...currentUni.master.map((c) => ({ c, g: "Master" }))]
    : [];

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.gdpr_consent) {
      toast.error("Devi accettare l'informativa privacy per procedere.");
      return;
    }
    setLoading(true);
    try {
      const res = await submitContact({ ...form, type: tab });
      toast.success(res.message || "Richiesta inviata!");
      setSubmitted(true);
      setForm(empty());
    } catch (err) {
      const msg = err?.response?.data?.detail || "Errore durante l'invio. Riprova.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="page-contatti">
      <PageHero
        eyebrow="Contatti & Iscrizioni"
        title="Parliamone, anche Online!"
        sub="Compila il form per richiedere informazioni o avviare l'iscrizione. Ti contatteremo entro 48 ore lavorative."
      />

      <section className="relative py-16 lg:py-24 bg-white overflow-hidden">
        <div className="absolute top-0 inset-x-0 h-64 bg-gradient-to-b from-brand-surfaceAlt to-transparent pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-10">
          {/* Form */}
          <div className="lg:col-span-7">
            <div className="relative rounded-[28px] p-[3px] bg-gradient-to-br from-brand-emerald via-brand-gold to-brand-emerald shadow-[0_30px_80px_-20px_rgba(11,26,53,0.25)]">
              <div className="rounded-[26px] bg-brand-cream overflow-hidden">
                <div className="p-7 lg:p-10">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-1.5 h-6 rounded-full bg-brand-emerald" />
                  <div className="eyebrow text-brand-emerald">Form di contatto</div>
                </div>
                <h2 className="font-heading font-extrabold text-2xl sm:text-3xl text-brand-navy">
                  Iniziamo il tuo percorso.
                </h2>
                <p className="text-brand-muted text-sm mt-1.5">Risposta del tutor entro 48 ore lavorative.</p>

                <div className="mt-6 inline-flex p-1.5 rounded-full bg-white/80 backdrop-blur border border-brand-line shadow-sm" data-testid="form-tabs">
                  {TABS.map((t) => (
                    <button
                      key={t.id}
                      onClick={() => { setTab(t.id); setSubmitted(false); }}
                      className={`px-5 py-2 rounded-full text-sm font-semibold transition-all duration-300 ${
                        tab === t.id
                          ? "bg-brand-navy text-white shadow-md"
                          : "text-brand-ink hover:text-brand-navy"
                      }`}
                      data-testid={`tab-${t.id}`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>

                {submitted ? (
                  <div className="mt-7 rounded-2xl bg-white border border-brand-emerald/30 p-10 text-center" data-testid="form-success">
                    <div className="mx-auto w-16 h-16 rounded-2xl bg-brand-emerald/10 flex items-center justify-center">
                      <CheckCircle2 className="w-9 h-9 text-brand-emerald" />
                    </div>
                    <h3 className="font-heading font-extrabold text-2xl text-brand-navy mt-5">Grazie!</h3>
                    <p className="text-brand-muted mt-2">La tua richiesta è stata inviata. Un tutor del Polo ti contatterà entro 48 ore lavorative.</p>
                    <button onClick={() => setSubmitted(false)} className="btn-secondary mt-6" data-testid="form-new">
                      Invia un'altra richiesta
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="mt-7 grid sm:grid-cols-2 gap-4" data-testid={`form-${tab}`}>
                    <Field label="Nome *" testid="field-nome">
                      <input required value={form.nome} onChange={(e) => update("nome", e.target.value)} className={inputCls} data-testid="input-nome" placeholder="Mario" />
                    </Field>
                    <Field label="Cognome *" testid="field-cognome">
                      <input required value={form.cognome} onChange={(e) => update("cognome", e.target.value)} className={inputCls} data-testid="input-cognome" placeholder="Rossi" />
                    </Field>
                    <Field label="Email *" testid="field-email">
                      <input type="email" required value={form.email} onChange={(e) => update("email", e.target.value)} className={inputCls} data-testid="input-email" placeholder="mario.rossi@email.it" />
                    </Field>
                    <Field label="Telefono *" testid="field-telefono">
                      <input required value={form.telefono} onChange={(e) => update("telefono", e.target.value)} className={inputCls} data-testid="input-telefono" placeholder="333 1234567" />
                    </Field>
                    <Field label="Luogo di Residenza *" testid="field-residenza" full>
                      <input required value={form.residenza} onChange={(e) => update("residenza", e.target.value)} className={inputCls} placeholder="Es. Sassari, SS" data-testid="input-residenza" />
                    </Field>

                    <div className="sm:col-span-2 my-1.5 flex items-center gap-3">
                      <div className="flex-1 h-px bg-brand-line" />
                      <span className="eyebrow text-brand-muted">Percorso accademico</span>
                      <div className="flex-1 h-px bg-brand-line" />
                    </div>

                    <Field label="Università interessata *" testid="field-universita">
                      <select required value={form.universita} onChange={(e) => { update("universita", e.target.value); update("corso", ""); }} className={inputCls} data-testid="select-universita">
                        <option value="">Seleziona ateneo...</option>
                        {UNIVERSITIES.map((u) => <option key={u.id} value={u.id}>{u.short}</option>)}
                      </select>
                    </Field>
                    <Field label="Corso interessato *" testid="field-corso">
                      <select required value={form.corso} onChange={(e) => update("corso", e.target.value)} className={inputCls} disabled={!currentUni} data-testid="select-corso">
                        <option value="">{currentUni ? "Seleziona corso..." : "Seleziona prima l'università"}</option>
                        {courseOptions.map((o, idx) => <option key={`${o.g}-${idx}-${o.c}`} value={o.c}>[{o.g}] {o.c}</option>)}
                      </select>
                    </Field>

                    {tab === "enrollment" && (
                      <Field label="Modalità di Iscrizione *" testid="field-modalita" full>
                        <select required value={form.modalita_iscrizione} onChange={(e) => update("modalita_iscrizione", e.target.value)} className={inputCls} data-testid="select-modalita">
                          <option value="">Seleziona convenzione...</option>
                          {MODALITA.map((m) => <option key={m} value={m}>{m}</option>)}
                        </select>
                      </Field>
                    )}

                    <Field label="Informazioni aggiuntive" testid="field-messaggio" full>
                      <textarea rows={4} value={form.messaggio} onChange={(e) => update("messaggio", e.target.value)} className={inputCls + " resize-none"} placeholder="Racconta brevemente le tue esigenze..." data-testid="input-messaggio" />
                    </Field>

                    <div className="sm:col-span-2 rounded-2xl bg-white border border-brand-line p-4">
                      <label className="flex gap-3 items-start cursor-pointer">
                        <input type="checkbox" checked={form.gdpr_consent} onChange={(e) => update("gdpr_consent", e.target.checked)} className="mt-0.5 w-5 h-5 rounded accent-brand-emerald" data-testid="checkbox-gdpr" />
                        <span className="text-sm text-brand-muted leading-relaxed">
                          Ho letto l'<Link to="/privacy-policy" className="text-brand-navy font-semibold underline">informativa sulla privacy</Link> e acconsento al trattamento dei dati ai sensi del GDPR.
                        </span>
                      </label>
                    </div>

                    <div className="sm:col-span-2 flex items-center justify-between gap-4 mt-2">
                      <div className="text-xs text-brand-muted hidden sm:block">
                        Risposta garantita entro <span className="font-bold text-brand-navy">48 ore lavorative</span>
                      </div>
                      <button type="submit" disabled={loading} className="btn-primary disabled:opacity-60 text-base px-7 py-3.5" data-testid="form-submit">
                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        {tab === "enrollment" ? "Iscriviti Ora" : "Invia Richiesta"}
                      </button>
                    </div>
                  </form>
                )}
              </div>
              </div>
            </div>

            {/* Reassurance row */}
            <div className="mt-6 grid grid-cols-3 gap-3 text-center" data-testid="form-reassurance">
              {[
                { v: "48h", l: "Tempo di risposta" },
                { v: "100%", l: "Gratuito" },
                { v: "Tutor", l: "In presenza" },
              ].map((r) => (
                <div key={r.l} className="rounded-2xl bg-brand-surface border border-brand-line px-3 py-3">
                  <div className="font-heading font-extrabold text-xl text-brand-emerald">{r.v}</div>
                  <div className="text-[11px] uppercase tracking-wider text-brand-muted font-semibold mt-0.5">{r.l}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-5 space-y-5">
            <div className="relative rounded-3xl bg-gradient-to-br from-brand-navyDark via-brand-navy to-brand-navyDark text-white p-8 overflow-hidden border border-white/5" data-testid="contact-info">
              <div className="absolute -top-20 -right-20 w-60 h-60 rounded-full bg-brand-gold/10 blur-3xl" />
              <div className="absolute -bottom-20 -left-20 w-60 h-60 rounded-full bg-brand-emerald/10 blur-3xl" />
              <div className="relative">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-14 h-14 rounded-xl bg-white p-1.5 flex items-center justify-center">
                    <img src={LOGOS.europa} alt="Istituto Europa" className="w-full h-full object-contain" />
                  </div>
                  <div>
                    <h3 className="font-heading font-extrabold text-2xl">Polo ECP Profili</h3>
                    <p className="text-white/70 text-xs">Istituto Europa – Fondazione Profili</p>
                  </div>
                </div>
              <ul className="mt-6 space-y-4 text-sm">
                <li className="flex gap-3"><MapPin className="w-5 h-5 text-brand-emerald shrink-0 mt-0.5" />
                  <div><div className="font-semibold">Indirizzo</div><div className="text-white/80">Via Forlanini 18, Sassari, Italia</div></div>
                </li>
                <li className="flex gap-3"><Phone className="w-5 h-5 text-brand-emerald shrink-0 mt-0.5" />
                  <div><div className="font-semibold">Telefono</div><a href="tel:+390792825126" className="text-white/80 hover:text-white">079 282 5126</a></div>
                </li>
                <li className="flex gap-3"><MessageCircle className="w-5 h-5 text-brand-emerald shrink-0 mt-0.5" />
                  <div><div className="font-semibold">WhatsApp Tutor</div><a href="https://wa.me/393465726648" target="_blank" rel="noreferrer" className="text-white/80 hover:text-white">346 572 6648</a></div>
                </li>
                <li className="flex gap-3"><Mail className="w-5 h-5 text-brand-emerald shrink-0 mt-0.5" />
                  <div><div className="font-semibold">Email Polo ECP</div><a href="mailto:uni.telematica@istitutoeuropasassari.it" className="text-white/80 hover:text-white break-all">uni.telematica@istitutoeuropasassari.it</a></div>
                </li>
              </ul>
              <div className="mt-6 pt-6 border-t border-white/10 flex gap-3">
                <a href="https://www.instagram.com/istitutoeuropa_sassari" target="_blank" rel="noreferrer" className="w-10 h-10 rounded-full bg-white/10 hover:bg-brand-emerald flex items-center justify-center" data-testid="contact-instagram"><Instagram className="w-4 h-4" /></a>
                <a href="https://www.facebook.com/istitutoeuropassassari" target="_blank" rel="noreferrer" className="w-10 h-10 rounded-full bg-white/10 hover:bg-brand-emerald flex items-center justify-center" data-testid="contact-facebook"><Facebook className="w-4 h-4" /></a>
              </div>
              </div>
            </div>

            <div className="rounded-3xl overflow-hidden border border-brand-line bg-white" data-testid="map-container">
              <iframe
                title="Mappa Polo ECP Profili"
                src="https://www.google.com/maps?q=Via+Forlanini+18,+Sassari&output=embed"
                width="100%"
                height="280"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
              <div className="p-4 text-sm">
                <div className="font-bold text-brand-navy">Come raggiungerci</div>
                <div className="text-brand-muted">Via Forlanini 18, 07100 Sassari (SS)</div>
              </div>
            </div>

            <div className="rounded-3xl border border-brand-line bg-white p-6" data-testid="atenei-partner">
              <div className="eyebrow text-brand-emerald mb-4">Atenei convenzionati</div>
              <div className="grid grid-cols-3 gap-4 items-center">
                <img src={LOGOS.pegaso} alt="Pegaso" className="h-10 object-contain mx-auto grayscale hover:grayscale-0 transition-all" />
                <img src={LOGOS.mercatorum} alt="Mercatorum" className="h-10 object-contain mx-auto grayscale hover:grayscale-0 transition-all" />
                <img src={LOGOS.sanraffaele} alt="San Raffaele" className="h-10 object-contain mx-auto grayscale hover:grayscale-0 transition-all" />
              </div>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}

const inputCls =
  "w-full h-12 px-4 rounded-xl border border-brand-line bg-white text-brand-ink placeholder-brand-muted focus:outline-none focus:ring-2 focus:ring-brand-emerald focus:border-brand-emerald disabled:opacity-60 disabled:cursor-not-allowed transition-all";

function Field({ label, children, full, testid }) {
  return (
    <div className={full ? "sm:col-span-2" : ""} data-testid={testid}>
      <label className="block text-[11px] font-bold tracking-[0.18em] uppercase text-brand-navy mb-1.5">{label}</label>
      {children}
    </div>
  );
}
